/* eslint-disable react/sort-comp */
import {
    BodyText,
    Checkbox,
    FormNote,
    Flex,
    IconV2,
    Label,
    LayoutBox,
    Select,
} from '@bamboohr/fabric';
import { toCamelCase } from 'BambooHR.util';
import { Component, createRef, Fragment } from 'react';

import { isEnabled } from 'FeatureToggle.util';

import '../state-withholding.react/state-withholding.styl';
import { AdditionalWithholding } from '../additional-withholding.react';
import { LinkedSelects } from '../linked-selects.react';
import { OptionalTextInput } from '../optional-text-input.react';
import { OptionalCheckbox } from '../optional-checkbox.react';
import { OptionalSelect } from '../optional-select.react';
import { OptionalUsdInput } from '../optional-usd-input/optional-usd-input';
import { SectionStyledBox } from '../section-styled-box';
import { SectionStyledHeader } from '../section-styled-header';

const feAccessLevelsQuickForms = isEnabled('PAY_INFO_TAB_ACCESS_LEVELS');

const OPTIONAL_FIELDS_MAP = {
    text: OptionalTextInput,
    checkbox: OptionalCheckbox,
    select: OptionalSelect,
    linkedSelects: LinkedSelects,
    usd: OptionalUsdInput,
};

export class IowaStateWithholding extends Component {
    constructor(props) {
        super(props);
        this.iconBtnRef = createRef();
        let filingStatus = null;
        let ignore = false;
        let selectedFilingStatus = null;
        let stateAbbreviation = null;
        const activeLocation = props.selectedLocation ? props.locations.byId[props.selectedLocation] : null;

        if (activeLocation) {
            selectedFilingStatus = activeLocation.newFields.filingStatus ? activeLocation.newFields.filingStatus.find(status => status.selected === true) : null;
            filingStatus = selectedFilingStatus ? selectedFilingStatus.val : null;
            ignore = activeLocation.newFields.ignoreStatusExemptions ? activeLocation.newFields.ignoreStatusExemptions.checked : false;
            stateAbbreviation = activeLocation.state;
        }

        // eslint-disable-next-line react/state-in-constructor
        this.state = {
            filingStatus,
            ignore,
            selectedLocation: props.selectedLocation,
            stateAbbreviation
        };
    }

    render() {
        const { ignore, selectedLocation, stateAbbreviation } = this.state;
        const {
            accessLevel,
            editPermission,
            headerHeadlineSize,
            isAdminUser,
            isPayrollAdminUser,
            locations,
            sectionNote,
            showWithholding,
            usesSectionComponent,
            viewOnly,
        } = this.props;
        const activeLocation = locations.byId[selectedLocation];

        let optionalFields = null;
        if (activeLocation && activeLocation.newFields.optionalFields) {
            optionalFields = activeLocation.newFields.optionalFields.allIds.map((id) => {
                const field = activeLocation.newFields.optionalFields.byId[id];
                const FieldComponent = OPTIONAL_FIELDS_MAP[field.type];
                const biId = `stateTaxWithholding-${stateAbbreviation}-${toCamelCase(field.data.label)}-${id}`;

                if (field.type === "usd" && field.data.label === "Total Allowances") { // *NOTE: Handle as an "Exemption" field.
                    return (
                        <FieldComponent
                            biId={biId}
                            data={ field.data }
                            help={ field.help }
                            id={ id }
                            isExemption={ true }
                            key={ id }
                            viewOnly={ viewOnly || ignore }
                        />
                    );
                }

                return (
                    <FieldComponent
                        biId={biId}
                        data={ field.data }
                        help={ field.help }
                        id={ id }
                        key={ id }
                        required={ field.required }
                        viewOnly={ viewOnly }
                    />
                );
            });
        }

        const fieldSetClasses = '';

        return (
            <SectionStyledBox
                hidden={ showWithholding === false }
                usesSectionComponent={ usesSectionComponent }
            >
                <fieldset className={ fieldSetClasses }>
                    <SectionStyledHeader
                        hasPopover={ true }
                        headlineSize={ headerHeadlineSize }
                        popoverContent={ $.__('This determines how much you will withhold from your paycheck for state taxes.')
                        }
                        popoverIcon="circle-question-regular"
                        popoverPlacement="bottom"
                        popoverTitle={ $.__('State Tax Withholding') }
                    >
                        { $.__('State Tax Withholding') }
                    </SectionStyledHeader>

                    { sectionNote ? <Flex
                        alignItems="center"
                        flexDirection="row"
                        gap={ 1 }
                    >
                        <IconV2
                            color="info-medium"
                            name="circle-info-solid"
                            size={ 16 }
                        />
                        <BodyText
                            color="info-medium"
                            size='small'
                            weight="semibold"
                        >
                            { sectionNote }
                        </BodyText>
                    </Flex> : null }

                    <Flex flexDirection="column" gap={2} marginTop={2}>

                        { locations && this._renderLocationsSelect() }

                        { activeLocation &&
                            <Fragment>
                                    { activeLocation.newFields.filingStatus && this._renderFilingStatusSelect(activeLocation.newFields.filingStatus) }
                                    { feAccessLevelsQuickForms && accessLevel !== undefined ? (
                                        <>
                                            { ((editPermission || viewOnly) && activeLocation.newFields.ignoreStatusExemptions) && this._renderIgnoreCheckbox(activeLocation.newFields) }
                                        </>
                                    ) : (
                                        <>
                                            { ((isAdminUser || isPayrollAdminUser || editPermission) && activeLocation.newFields.ignoreStatusExemptions) && this._renderIgnoreCheckbox(activeLocation.newFields) }
                                        </>
                                    ) }
                                { optionalFields }
                                { activeLocation.newFields.additionalWithholding && <AdditionalWithholding data={ activeLocation.newFields.additionalWithholding } type="state" viewOnly={ viewOnly } /> }
                            </Fragment>
                        }
                    </Flex>
                </fieldset>
            </SectionStyledBox>
        );
    }

    _handleLocationChange = (selectedValues) => {
        const {
            locations,
            onLocationChange,
            setSelectedStateLocation,
        } = this.props;

        const [selectedValue = ''] = selectedValues;
        
        setSelectedStateLocation(selectedValue);

        this.setState({
            selectedLocation: selectedValue,
            stateAbbreviation: selectedValue ? locations.byId[selectedValue]?.state : null,
        });

        
        if (onLocationChange) {
            onLocationChange({
                val: selectedValue,
            });
        }
    };

    _renderLocationsSelect = () => {
        const {
            locations,
            newHirePacket,
            showLocationRequiredAsterisk,
            viewOnly,
        } = this.props;
        const { stateAbbreviation, selectedLocation } = this.state;

        return (
            <Flex flexDirection="column">
                <Label disabled={ viewOnly || newHirePacket } htmlFor="stateLocation" require={ showLocationRequiredAsterisk }>{ $.__('Location') }</Label>
                <Flex alignItems="center" gap={1}>
                    <Select
                        id="stateLocation"
                        isClearable={ true }
                        isDisabled={ viewOnly || newHirePacket }
                        items={
                            locations.allIds.map((location) => {
                                return {
                                    text: locations.byId[location].displayText,
                                    value: locations.byId[location].val,
                                };
                            })
                        }
                        name="state[location]"
                        onChange={ this._handleLocationChange }
                        required={ showLocationRequiredAsterisk }
                        selectedValues={ [selectedLocation] }
                        width="5"
                    />
                { stateAbbreviation && <FormNote>{ $.__('State: %1$s', stateAbbreviation) }</FormNote> }
                </Flex>
            </Flex>
        );
    };

    _renderFilingStatusSelect = (filingStatus) => {
        const { viewOnly } = this.props;
        const {
            filingStatus: selectedFilingStatus,
            ignore,
        } = this.state;

        return (
            <LayoutBox>
                <Label disabled={ ignore || viewOnly } htmlFor="stateStatus" required={ !ignore } >{ $.__('Filing Status') }</Label>
                <Select
                    id="stateStatus"
                    isClearable={ true }
                    isDisabled={ ignore || viewOnly }
                    items={
                        filingStatus.map((status) => {
                            return {
                                text: ignore ? 'NA' : status.displayText,
                                value: status.val,
                            };
                        })
                    }
                    name="state[status]"
                    onChange={ (value) => {
                        this.setState({
                            filingStatus: value[0],
                        });
                    } }
                    placeholder={ ignore ? 'NA' : undefined }
                    required={ !ignore }
                    selectedValues={ [selectedFilingStatus] }
                    width={ 8 }
                />
            </LayoutBox>
        );
    };

    _renderIgnoreCheckbox = () => {
        const {
            ignore,
            viewOnly
        } = this.state;
        const labelText = $.__('Ignore filing status/other exemptions');

        return (
            <Checkbox
                checked={ ignore }
                disabled={ viewOnly }
                htmlFor="stateIgnoreCheckbox"
                id="stateIgnoreCheckbox"
                label={ labelText }
                name="state[taxExempt]"
                onChange={ (event) => this.setState({
                    ignore: event.checked
                })
                }
                value={ ignore ? 'on' : 'off' }
            />
        );
    };

}
